import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useConfig } from "@/hooks/useConfig";
import { api, money, apiError, telHref, smsHref } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Phone, MessageSquare, ArrowLeft, ArrowRight, Check, Car, Home, MapPin, CalendarDays, Clock, Layers, ShieldCheck, CreditCard, Store } from "lucide-react";
import { CAR_MAKES, MODELS_BY_MAKE, YEARS } from "@/lib/vehicles";

function AutoInput({ value, onChange, options, placeholder, testid }) {
  const [open, setOpen] = useState(false);
  const q = (value || "").toLowerCase();
  const filtered = (options || []).filter((o) => o.toLowerCase().includes(q)).slice(0, 8);
  return (
    <div className="relative">
      <Input data-testid={testid} autoComplete="off" className="rounded-none bg-white border-slate-200 mt-1"
        value={value} placeholder={placeholder}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)} />
      {open && filtered.length > 0 && (
        <div data-testid={`${testid}-list`} className="absolute z-40 left-0 right-0 bg-white border border-slate-200 shadow-lg max-h-56 overflow-auto">
          {filtered.map((o) => (
            <button type="button" key={o} data-testid={`${testid}-opt`}
              className="block w-full text-left px-3 py-2 text-sm hover:bg-slate-100"
              onMouseDown={(e) => { e.preventDefault(); onChange(o); setOpen(false); }}>{o}</button>
          ))}
        </div>
      )}
    </div>
  );
}

const STEPS = ["Service", "Vehicle", "Film", "Add-Ons", "Date & Time", "Your Info", "Payment"];

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function addDays(base, n) {
  const d = new Date(base);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function fmtDate(s) {
  const [y, m, d] = s.split("-");
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}
function fmtTime(t) {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr}:${String(m).padStart(2, "0")} ${ap}`;
}

const Card = ({ active, disabled, onClick, children, testid }) => (
  <button type="button" data-testid={testid} disabled={disabled} onClick={onClick}
    className={`text-left w-full border p-5 transition-colors ${active ? "border-primary bg-primary/10" : "border-slate-200 bg-white hover:border-slate-400"} ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}>
    {children}
  </button>
);

export default function Booking() {
  const { data, isLoading } = useConfig();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [confirmed, setConfirmed] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [availMsg, setAvailMsg] = useState(null);

  const [form, setForm] = useState({
    service_id: "", vehicle_type_id: "", film_id: "", percentage_id: "",
    vehicle_year: "", vehicle_make: "", vehicle_model: "",
    location: "shop", remove_old_tint: false, windshield: false, extra_addon: false,
    payment_method: "shop", date: "", time: "",
    customer_name: "", phone: "", email: "", notes: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const service = useMemo(() => data?.services.find((s) => s.id === form.service_id), [data, form.service_id]);
  const film = useMemo(() => data?.films?.find((f) => f.id === form.film_id), [data, form.film_id]);
  const pct = useMemo(() => data?.percentages.find((p) => p.id === form.percentage_id), [data, form.percentage_id]);
  const vtype = useMemo(() => data?.vehicle_types.find((v) => v.id === form.vehicle_type_id), [data, form.vehicle_type_id]);

  const requiresFilm = !!service?.requires_film;
  const requiresPct = !!service?.requires_percentage;
  const onlineBookable = service ? service.online_bookable !== false : true;

  const allowedPcts = useMemo(() => {
    if (!data || !service) return [];
    return data.percentages.filter((p) => (service.allowed_percentages || []).includes(p.id));
  }, [data, service]);

  const adjAmount = vtype?.price_adjustment_amount || 0;
  const rawBase = service?.base_price ?? (service?.prices ? service.prices[form.vehicle_type_id] : 0) ?? 0;
  const basePrice = service && form.vehicle_type_id ? Math.round((rawBase + adjAmount) * 100) / 100 : 0;
  const filmPrice = requiresFilm && film ? film.price || 0 : 0;
  const pctPrice = requiresPct && pct ? pct.price || 0 : 0;
  const wsAddon = data?.settings?.windshield_addon;
  const windshieldPrice = form.windshield && wsAddon?.enabled ? wsAddon.price || 0 : 0;
  const extraCfg = data?.settings?.extra_addon;
  const extraPrice = form.extra_addon && extraCfg?.enabled ? extraCfg.price || 0 : 0;
  const _mk = (form.vehicle_make || "").trim().toLowerCase();
  const _modelKey = CAR_MAKES.find((m) => m.toLowerCase() === _mk);
  const modelOptions = _modelKey ? (MODELS_BY_MAKE[_modelKey] || []) : [];
  const removalPrice = form.remove_old_tint && data?.settings?.old_tint_removal?.enabled ? data.settings.old_tint_removal.price : 0;
  const mobileFee = form.location === "mobile" ? data?.settings?.mobile_fee || 0 : 0;
  const subtotal = (basePrice || 0) + filmPrice + pctPrice + windshieldPrice + extraPrice + (removalPrice || 0) + (mobileFee || 0);
  const taxCfg = data?.settings?.tax;
  const taxAmount = taxCfg?.enabled ? Math.round(subtotal * (taxCfg.rate || 0)) / 100 : 0;
  const total = subtotal + taxAmount;

  useEffect(() => {
    if (step === 4 && form.date && onlineBookable) {
      setSlotsLoading(true);
      api.get(`/availability?date=${form.date}&service_id=${form.service_id}&windshield=${form.windshield}`)
        .then((r) => { setSlots(r.data.slots || []); setAvailMsg(r.data.slots?.length ? null : (r.data.message || "No times available")); })
        .catch(() => { setSlots([]); setAvailMsg("No times available"); })
        .finally(() => setSlotsLoading(false));
    }
  }, [step, form.date, form.service_id, form.windshield, onlineBookable]);

  if (isLoading || !data) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  const { settings } = data;

  if (settings.online_booking_enabled === false) {
    return (
      <div className="min-h-screen bg-slate-50 grid place-items-center px-5 text-center">
        <div className="max-w-md">
          <h1 className="text-3xl font-head font-black uppercase tracking-tighter" data-testid="booking-disabled">Online Booking Is Currently Unavailable</h1>
          <p className="mt-4 text-muted-foreground">{settings.online_booking_message || "Please call or text us to book your appointment."}</p>
          <div className="mt-8 flex gap-3 justify-center">
            <a href={telHref(settings.phone)}><Button className="rounded-none bg-primary text-primary-foreground font-bold uppercase"><Phone className="w-4 h-4 mr-2" />Call</Button></a>
            <a href={smsHref(settings.sms_number)}><Button variant="outline" className="rounded-none border-slate-300 bg-transparent"><MessageSquare className="w-4 h-4 mr-2" />Text</Button></a>
          </div>
          <button onClick={() => navigate("/")} className="mt-6 text-xs uppercase tracking-wide text-muted-foreground hover:text-primary">← Back to site</button>
        </div>
      </div>
    );
  }

  const isValidYear = (y) => /^\d{4}$/.test(String(y).trim()) && +y >= 1980 && +y <= 2027;
  const usDigits = (p) => (p || "").replace(/\D/g, "");
  const isValidUSPhone = (p) => { const d = usDigits(p); return d.length === 10 || (d.length === 11 && d[0] === "1"); };
  const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || "").trim());
  const isValidName = (n) => (n || "").trim().length >= 2;
  const vehicleValid = !!form.vehicle_type_id && isValidYear(form.vehicle_year) && form.vehicle_make.trim().length >= 2 && form.vehicle_model.trim().length >= 1;
  const infoValid = isValidName(form.customer_name) && isValidUSPhone(form.phone) && isValidEmail(form.email);

  const canNext = () => {
    switch (step) {
      case 0: return !!form.service_id;
      case 1: return vehicleValid;
      case 2: return requiresFilm ? !!form.film_id : true;
      case 3: return !!form.location;
      case 4: return onlineBookable && !!form.date && !!form.time;
      case 5: return infoValid;
      case 6: return !!form.payment_method;
      default: return true;
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data: appt } = await api.post("/bookings", {
        ...form,
        email: form.email || null,
        film_id: requiresFilm ? form.film_id : null,
        percentage_id: requiresPct ? form.percentage_id : null,
      });
      if (form.payment_method === "online") {
        const { data: co } = await api.post("/payments/checkout", { appointment_id: appt.id, origin_url: window.location.origin });
        window.location.href = co.checkout_url;
        return;
      }
      setConfirmed(appt);
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(apiError(e));
      setSubmitting(false);
    }
  };

  if (confirmed) return <Confirmation appt={confirmed} settings={settings} navigate={navigate} />;

  const dates = Array.from({ length: 28 }).map((_, i) => addDays(todayStr(), i));

  return (
    <div className="min-h-screen bg-slate-50 pb-28">
      <div className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 shadow-sm border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-5 h-16 flex items-center justify-between">
          <button data-testid="booking-back-home" onClick={() => navigate("/")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary">
            <span className="w-8 h-8 grid place-items-center bg-primary text-primary-foreground font-head font-black text-sm">WM</span>
            <span className="font-head font-extrabold uppercase tracking-tight text-slate-900 text-sm">Window Tint</span>
          </button>
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Book Appointment</span>
        </div>
        <div className="max-w-3xl mx-auto px-5 pb-3">
          <div className="flex gap-1">
            {STEPS.map((s, i) => <div key={s} className={`h-1 flex-1 ${i <= step ? "bg-primary" : "bg-white/10"}`} />)}
          </div>
          <p className="mt-2 text-xs uppercase tracking-[0.2em] text-primary">Step {step + 1} / {STEPS.length} — {STEPS[step]}</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-5 py-8">
        {step === 0 && (
          <div className="space-y-4 fade-up">
            <h2 className="text-2xl font-head font-black uppercase tracking-tight">Choose a Service</h2>
            {data.services.filter((s) => s.available).map((s) => (
              <Card key={s.id} testid={`book-service-${s.id}`} active={form.service_id === s.id} disabled={!s.available}
                onClick={() => { set("service_id", s.id); set("percentage_id", ""); set("film_id", ""); }}>
                <div className="flex justify-between items-start gap-3">
                  <div>
                    <p className="font-head font-bold uppercase">{s.name}</p>
                    <p className="text-sm text-muted-foreground mt-1">{s.description}</p>
                    {s.duration && <p className="text-xs text-primary mt-2 uppercase tracking-wide"><Clock className="w-3 h-3 inline mr-1" />{s.duration}</p>}
                  </div>
                  <div className="text-right shrink-0">
                    {s.available
                      ? <span className="font-head font-black text-primary">{s.price_display || `${money(s.base_price ?? 0)}+`}</span>
                      : <span className="text-xs uppercase text-destructive">Currently<br />Not Available</span>}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {step === 1 && (
          <div className="space-y-5 fade-up">
            <h2 className="text-2xl font-head font-black uppercase tracking-tight">Your Vehicle</h2>
            <div className="grid grid-cols-2 gap-3">
              {data.vehicle_types.map((v) => (
                <Card key={v.id} testid={`book-vtype-${v.id}`} active={form.vehicle_type_id === v.id} onClick={() => set("vehicle_type_id", v.id)}>
                  <Car className="w-5 h-5 text-primary mb-2" />
                  <p className="font-head font-bold uppercase text-sm">{v.name}</p>
                </Card>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs uppercase tracking-wide text-muted-foreground">Year</Label>
                <AutoInput testid="book-vehicle-year" value={form.vehicle_year} onChange={(v) => set("vehicle_year", v)} options={YEARS} placeholder="2022" /></div>
              <div><Label className="text-xs uppercase tracking-wide text-muted-foreground">Make</Label>
                <AutoInput testid="book-vehicle-make" value={form.vehicle_make} onChange={(v) => set("vehicle_make", v)} options={CAR_MAKES} placeholder="Type e.g. T…" /></div>
              <div><Label className="text-xs uppercase tracking-wide text-muted-foreground">Model</Label>
                <AutoInput testid="book-vehicle-model" value={form.vehicle_model} onChange={(v) => set("vehicle_model", v)} options={modelOptions} placeholder="Model" /></div>
            </div>
            {!vehicleValid && (form.vehicle_year || form.vehicle_make || form.vehicle_model) && (
              <p data-testid="vehicle-error" className="text-xs text-red-600">Please select your vehicle type and enter a valid year (e.g. 2022), make, and model to continue.</p>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4 fade-up">
            <h2 className="text-2xl font-head font-black uppercase tracking-tight">Select Film Type</h2>
            {!requiresFilm ? (
              <p className="text-sm text-muted-foreground border border-slate-200 bg-white p-5">No film selection needed for {service?.name}. Tap Continue.</p>
            ) : (
              <div className="grid gap-3">
                {data.films?.map((f) => (
                  <Card key={f.id} testid={`book-film-${f.id}`} active={form.film_id === f.id} disabled={!f.available} onClick={() => set("film_id", f.id)}>
                    <div className="flex justify-between items-start gap-3">
                      <div>
                        <p className="font-head font-bold uppercase flex items-center gap-2"><Layers className="w-4 h-4 text-primary" />{f.name}</p>
                        <p className="text-sm text-muted-foreground mt-1">{f.description}</p>
                      </div>
                      <div className="text-right shrink-0">
                        {f.available ? <span className="text-sm text-primary font-semibold">{money((basePrice || service?.base_price || 0) + (f.price || 0))}</span>
                          : <span className="text-xs uppercase text-destructive">Currently<br />Not Available</span>}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4 fade-up">
            <h2 className="text-2xl font-head font-black uppercase tracking-tight">Location & Add-Ons</h2>
            <div className="grid grid-cols-2 gap-3">
              <Card testid="book-loc-shop" active={form.location === "shop"} onClick={() => set("location", "shop")}>
                <Home className="w-5 h-5 text-primary mb-2" />
                <p className="font-head font-bold uppercase text-sm">Shop Appointment</p>
                <p className="text-xs text-muted-foreground mt-1">Come to our Fresno shop</p>
              </Card>
              <Card testid="book-loc-mobile" active={form.location === "mobile"} onClick={() => set("location", "mobile")}>
                <MapPin className="w-5 h-5 text-primary mb-2" />
                <p className="font-head font-bold uppercase text-sm">Mobile Service</p>
                <p className="text-xs text-muted-foreground mt-1">We come to you (+{money(settings.mobile_fee)})</p>
              </Card>
            </div>
            {wsAddon?.enabled && requiresFilm && service?.name !== "Windshield Tint" && (
              <label data-testid="book-windshield-toggle" className="flex items-center gap-3 border border-slate-200 bg-white p-4 cursor-pointer">
                <Checkbox checked={form.windshield} onCheckedChange={(v) => set("windshield", !!v)} className="rounded-none border-white/30" />
                <div><p className="font-head font-bold uppercase text-sm">Add Windshield</p>
                  <p className="text-xs text-muted-foreground">Include the front windshield (+{money(wsAddon.price)})</p></div>
              </label>
            )}
            {settings.old_tint_removal?.enabled && (
              <label data-testid="book-removal-toggle" className="flex items-center gap-3 border border-slate-200 bg-white p-4 cursor-pointer">
                <Checkbox checked={form.remove_old_tint} onCheckedChange={(v) => set("remove_old_tint", !!v)} className="rounded-none border-white/30" />
                <div><p className="font-head font-bold uppercase text-sm">Old Tint Removal</p>
                  <p className="text-xs text-muted-foreground">I need existing tint removed first (+{money(settings.old_tint_removal.price)}). {settings.old_tint_removal.duration}</p></div>
              </label>
            )}
            {extraCfg?.enabled && (
              <label data-testid="book-extra-toggle" className="flex items-center gap-3 border border-slate-200 bg-white p-4 cursor-pointer">
                <Checkbox checked={form.extra_addon} onCheckedChange={(v) => set("extra_addon", !!v)} className="rounded-none border-white/30" />
                <div><p className="font-head font-bold uppercase text-sm">{extraCfg.label || "Add-On"}</p>
                  <p className="text-xs text-muted-foreground">+{money(extraCfg.price || 0)}</p></div>
              </label>
            )}
          </div>
        )}

        {step === 4 && (
          <div className="space-y-5 fade-up">
            <h2 className="text-2xl font-head font-black uppercase tracking-tight">Pick a Date & Time</h2>
            {!onlineBookable ? (
              <div data-testid="removal-call-required" className="border border-primary/40 bg-primary/5 p-6 text-center">
                <p className="font-head font-bold uppercase text-lg">Call to Schedule</p>
                <p className="text-sm text-muted-foreground mt-2">Please call WM Window Tint to schedule {service?.name}. Timing depends on your vehicle and the condition of the existing tint.</p>
                <a href={telHref(settings.phone)} className="inline-block mt-4">
                  <Button data-testid="removal-call-btn" className="rounded-none bg-primary text-primary-foreground font-bold uppercase"><Phone className="w-4 h-4 mr-2" />Call {settings.phone}</Button>
                </a>
              </div>
            ) : (
            <>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-2"><CalendarDays className="w-4 h-4" /> Date</Label>
              <div className="mt-2 flex gap-2 overflow-x-auto hide-scrollbar pb-2">
                {dates.map((d) => (
                  <button key={d} data-testid={`book-date-${d}`} onClick={() => { set("date", d); set("time", ""); }}
                    className={`shrink-0 w-20 py-3 border text-center ${form.date === d ? "border-primary bg-primary/10 text-slate-900" : "border-slate-200 bg-white text-muted-foreground hover:border-slate-400"}`}>
                    <span className="block text-xs uppercase">{fmtDate(d).split(",")[0]}</span>
                    <span className="block font-head font-bold text-sm mt-1">{fmtDate(d).split(" ").slice(1).join(" ")}</span>
                  </button>
                ))}
              </div>
            </div>
            {form.date && (
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-2"><Clock className="w-4 h-4" /> Time</Label>
                {slotsLoading ? <p className="mt-3 text-sm text-muted-foreground">Loading times…</p>
                  : slots.length === 0 ? <p data-testid="no-slots" className="mt-3 text-sm text-destructive uppercase tracking-wide">{availMsg}</p>
                  : (
                    <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {slots.map((t) => (
                        <button key={t} data-testid={`book-time-${t}`} onClick={() => set("time", t)}
                          className={`py-3 border text-sm font-semibold ${form.time === t ? "border-primary bg-primary/10 text-slate-900" : "border-slate-200 bg-white text-muted-foreground hover:border-slate-400"}`}>{fmtTime(t)}</button>
                      ))}
                    </div>
                  )}
              </div>
            )}
            </>
            )}
          </div>
        )}

        {step === 5 && (
          <div className="space-y-4 fade-up">
            <h2 className="text-2xl font-head font-black uppercase tracking-tight">Your Info</h2>
            <div><Label className="text-xs uppercase tracking-wide text-muted-foreground">Full Name *</Label>
              <Input data-testid="book-name" className="rounded-none bg-white border-slate-200 mt-1" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} placeholder="John Doe" />
              {form.customer_name && !isValidName(form.customer_name) && <p className="text-xs text-red-600 mt-1">Please enter your full name.</p>}</div>
            <div><Label className="text-xs uppercase tracking-wide text-muted-foreground">Phone (US) *</Label>
              <Input data-testid="book-phone" inputMode="tel" className="rounded-none bg-white border-slate-200 mt-1" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(559) 555-1234" />
              {form.phone && !isValidUSPhone(form.phone) && <p data-testid="phone-error" className="text-xs text-red-600 mt-1">Enter a valid 10-digit US phone number.</p>}</div>
            <div><Label className="text-xs uppercase tracking-wide text-muted-foreground">Email *</Label>
              <Input data-testid="book-email" inputMode="email" className="rounded-none bg-white border-slate-200 mt-1" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" />
              {form.email && !isValidEmail(form.email) && <p data-testid="email-error" className="text-xs text-red-600 mt-1">Enter a valid email address.</p>}</div>
            <div><Label className="text-xs uppercase tracking-wide text-muted-foreground">Notes (optional)</Label>
              <Textarea data-testid="book-notes" className="rounded-none bg-white border-slate-200 mt-1" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Anything we should know?" /></div>
          </div>
        )}

        {step === 6 && (
          <div className="space-y-4 fade-up">
            <h2 className="text-2xl font-head font-black uppercase tracking-tight">Payment & Review</h2>
            <div className={`grid ${settings.online_payments_enabled ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"} gap-3`}>
              <Card testid="book-pay-shop" active={form.payment_method === "shop"} onClick={() => set("payment_method", "shop")}>
                <Store className="w-5 h-5 text-primary mb-2" />
                <p className="font-head font-bold uppercase text-sm">Pay at Shop — Cash or Card</p>
                <p className="text-xs text-muted-foreground mt-1">Pay with cash or card when your install is done</p>
              </Card>
              {settings.online_payments_enabled && (
                <Card testid="book-pay-online" active={form.payment_method === "online"} onClick={() => set("payment_method", "online")}>
                  <CreditCard className="w-5 h-5 text-primary mb-2" />
                  <p className="font-head font-bold uppercase text-sm">Pay Online</p>
                  <p className="text-xs text-muted-foreground mt-1">Pay securely now with your card</p>
                </Card>
              )}
            </div>
            <p className="text-xs text-muted-foreground">{settings.payment_info?.cash_note} {settings.payment_info?.card_note} No online deposit required to book.</p>

            <div className="border border-slate-200 bg-white divide-y divide-white/5 mt-2">
              {[
                ["Service", service?.name],
                ...(`${form.vehicle_year} ${form.vehicle_make} ${form.vehicle_model}`.trim() ? [["Vehicle", `${form.vehicle_year} ${form.vehicle_make} ${form.vehicle_model}`.trim()]] : []),
                ["Vehicle Type", vtype?.name],
                ...(adjAmount ? [["SUV Adjustment", `+${money(adjAmount)}`]] : []),
                ...(requiresFilm ? [["Film", `${film?.name} ${money((basePrice || 0) + filmPrice)}`]] : []),
                ["Windshield", form.windshield ? `Included (+${money(windshieldPrice)})` : "Not included"],
                ...(form.remove_old_tint ? [["Old Tint Removal", `+${money(removalPrice)}`]] : []),
                ...(form.extra_addon && extraCfg?.enabled ? [[extraCfg.label || "Add-On", `+${money(extraPrice)}`]] : []),
                ["Location", form.location === "mobile" ? `Mobile (+${money(mobileFee)})` : "Shop"],
                ["Date", form.date ? fmtDate(form.date) : "—"],
                ["Time", form.time ? fmtTime(form.time) : "—"],
                ...(taxAmount ? [[`Sales Tax (${taxCfg?.rate || 0}%)`, money(taxAmount)]] : []),
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between px-4 py-3 text-sm">
                  <span className="text-muted-foreground uppercase tracking-wide text-xs">{k}</span>
                  <span className="font-semibold text-right">{v}</span>
                </div>
              ))}
            </div>
            <div className="border border-primary/40 bg-primary/5 p-4 flex justify-between items-center">
              <span className="uppercase tracking-wide text-sm text-muted-foreground">Total</span>
              <span className="font-head font-black text-2xl text-primary" data-testid="review-total">{money(total)}</span>
            </div>
            {settings.warranty_text && (
              <p className="text-xs text-primary flex items-start gap-2"><ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />{settings.warranty_text}</p>
            )}
          </div>
        )}
      </div>

      <div className="fixed bottom-0 inset-x-0 z-40 backdrop-blur-xl bg-white/90 shadow-[0_-2px_12px_rgba(0,0,0,0.06)] border-t border-slate-200">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between gap-3">
          <Button data-testid="book-prev-btn" variant="outline" className="rounded-none border-slate-300 bg-transparent"
            onClick={() => (step === 0 ? navigate("/") : setStep(step - 1))}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          {step < STEPS.length - 1 ? (
            <Button data-testid="book-next-btn" disabled={!canNext()} onClick={() => setStep(step + 1)}
              className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider px-8">
              Continue <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button data-testid="book-confirm-btn" disabled={submitting || !canNext()} onClick={submit}
              className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider px-8">
              {submitting ? "Booking…" : "Confirm Booking"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function Confirmation({ appt, settings, navigate }) {
  return (
    <div className="min-h-screen bg-slate-50 grid place-items-center px-5 py-16">
      <div className="max-w-lg w-full fade-up">
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto grid place-items-center bg-primary text-primary-foreground mb-4"><Check className="w-8 h-8" /></div>
          <h1 className="text-3xl font-head font-black uppercase tracking-tighter">You're Booked!</h1>
          <p className="text-muted-foreground mt-2 text-sm">A confirmation has been sent. We'll be in touch shortly.</p>
        </div>
        <div className="border border-slate-200 bg-white divide-y divide-white/5" data-testid="confirmation-card">
          {[
            ["Name", appt.customer_name],
            ["Service", appt.service_name],
            ["Vehicle", `${appt.vehicle_year || ""} ${appt.vehicle_make || ""} ${appt.vehicle_model || ""}`.trim() || appt.vehicle_type_name],
            ...(appt.film_name ? [["Film", appt.film_name]] : []),
            ...(appt.percentage_label ? [["Tint %", appt.percentage_label]] : []),
            ...(appt.windshield ? [["Windshield", "Yes"]] : []),
            ...(appt.remove_old_tint ? [["Old Tint Removal", "Yes"]] : []),
            ["Location", appt.location === "mobile" ? "Mobile Service" : "Shop Appointment"],
            ["Date", fmtDate(appt.date)],
            ["Time", fmtTime(appt.time)],
            ["Payment", appt.payment_method_label],
          ].map(([k, v]) => (
            <div key={k} className="flex justify-between px-4 py-3 text-sm">
              <span className="text-muted-foreground uppercase tracking-wide text-xs">{k}</span>
              <span className="font-semibold text-right">{v}</span>
            </div>
          ))}
          <div className="flex justify-between px-4 py-4 bg-primary/5">
            <span className="uppercase tracking-wide text-sm text-primary">Total</span>
            <span className="font-head font-black text-xl text-primary">{money(appt.total)}</span>
          </div>
        </div>
        {settings.warranty_text && (
          <div className="mt-4 border border-slate-200 bg-white p-4">
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-bold mb-1">5-Year Warranty</p>
            <p className="text-xs text-muted-foreground">{settings.warranty_text}</p>
          </div>
        )}
        {settings.curing_text && (
          <div className="mt-3 border border-slate-200 bg-white p-4">
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-bold mb-1">Curing Info</p>
            <p className="text-xs text-muted-foreground">{settings.curing_text}</p>
          </div>
        )}
        {settings.after_care_text && (
          <div className="mt-3 border border-slate-200 bg-white p-4">
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-bold mb-1">After-Installation Care</p>
            <p className="text-xs text-muted-foreground">{settings.after_care_text}</p>
          </div>
        )}
        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          <a href={telHref(settings.phone)}><Button variant="outline" className="rounded-none border-slate-300 bg-transparent"><Phone className="w-4 h-4 mr-2" />Call Us</Button></a>
          <a href={smsHref(settings.sms_number)}><Button variant="outline" className="rounded-none border-slate-300 bg-transparent"><MessageSquare className="w-4 h-4 mr-2" />Text Us</Button></a>
          <Button onClick={() => navigate("/")} className="rounded-none bg-primary text-primary-foreground font-bold uppercase">Done</Button>
        </div>
      </div>
    </div>
  );
}
